import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  format,
  isToday,
  isTomorrow,
  differenceInMinutes,
  differenceInCalendarDays,
  startOfDay,
  endOfDay,
} from 'date-fns';
import {
  Video,
  FileText,
  MoreVertical,
  User,
  CalendarPlus,
  Clock,
} from 'lucide-react';
import { PageHeader } from 'src/components/common/PageHeader';
import { PageContainer } from 'src/components/common/PageContainer';
import SearchInput from 'src/components/common/SearchInput';
import FilterButton from 'src/components/common/FilterButton';
import DateRangePicker from 'src/components/common/DateRangePicker';
import TablePagination from 'src/components/common/TablePagination';
import { Button } from 'src/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from 'src/components/ui/dropdown-menu';
import { useToast } from 'src/hooks/use-toast';
import { useAuth } from 'src/hooks/useAuth';
import { useInterviewerInterviews } from 'src/services/api/queries/interviewerQueries';

// Only "Online" is a confirmed value of the real API's `mode` field so far.
// Anything else falls back to the raw label in mapInterview() below rather
// than guessing an icon/meaning for an unconfirmed enum value.
const MODE_META = {
  video: { icon: Video, label: 'Video' },
};

const formatTime = (date) => format(date, 'h:mm a');

const relativeLabel = (date) => {
  const now = new Date();
  const minutes = differenceInMinutes(date, now);
  if (minutes < 0) return null;
  if (minutes < 60) return `in ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) {
    const rem = minutes % 60;
    return rem ? `in ${hours}h ${rem}min` : `in ${hours}h`;
  }
  const days = differenceInCalendarDays(date, now);
  return `in ${days}d`;
};

const dayGroupLabel = (date) => {
  if (isToday(date)) return 'Later today';
  if (isTomorrow(date)) return 'Tomorrow';
  return format(date, 'EEE d MMM');
};

// Groups a mapped-interview list by calendar day. `descending` sorts most
// recent day/time first — used for the two past-looking tabs (Scorecard due,
// Completed) so the newest item is what a returning interviewer sees first,
// instead of having to scroll past months of history.
const buildGroups = (list, descending = false) => {
  const byDay = new Map();
  list.forEach((iv) => {
    const key = format(iv.start, 'yyyy-MM-dd');
    if (!byDay.has(key)) byDay.set(key, []);
    byDay.get(key).push(iv);
  });
  return Array.from(byDay.entries())
    .sort(([a], [b]) => (descending ? b.localeCompare(a) : a.localeCompare(b)))
    .map(([key, items]) => ({
      key,
      label: dayGroupLabel(items[0].start),
      items: items.sort((a, b) =>
        descending ? b.start - a.start : a.start - b.start
      ),
    }));
};

// Raw /internal/interviewer/interviews row -> the shape this page renders.
// Match tier, a mode's venue/platform detail, and a brief's generation
// status all lived on the old mock but have no real backend yet — left out
// entirely here rather than invented; re-add once that data exists.
const mapInterview = (raw, currentUserEmail) => {
  const interviewers = raw.interviewers || [];
  const others = interviewers.filter(
    (p) =>
      (p.email || '').toLowerCase() !== (currentUserEmail || '').toLowerCase()
  );
  const viewerOnPanel = others.length !== interviewers.length;
  let panelLabel = null;
  if (interviewers.length > 0) {
    if (viewerOnPanel) {
      panelLabel = others.length
        ? `Panel: you and ${others.map((p) => p.name).join(', ')}`
        : 'Panel: just you';
    } else {
      panelLabel = `Panel: ${interviewers.map((p) => p.name).join(', ')}`;
    }
  }

  const modeKey = raw.mode === 'Online' ? 'video' : null;

  return {
    id: raw.interview_round_detail_id,
    secMappingId: raw.sec_mapping_id,
    candidateId: raw.candidate_id,
    candidateName: raw.candidate_name,
    candidateRegistrationId: raw.candidate_registration_id,
    role: raw.most_recent_job,
    company: raw.most_recent_company,
    requirementId: raw.requirement_id,
    requirementTitle: raw.requirement_name,
    registrationId: raw.requirement_registration_id,
    round: `Round ${raw.round_number}`,
    start: new Date(raw.start_time),
    durationMinutes: raw.duration_minutes,
    mode: modeKey,
    modeLabel: modeKey ? MODE_META[modeKey].label : raw.mode,
    joinLink: raw.join_link,
    panelLabel,
  };
};

function RowMenu({ interview, onAction }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          className="h-8 w-8"
          aria-label={`More actions for ${interview.candidateName}`}
        >
          <MoreVertical className="h-4 w-4 text-muted-foreground" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem
          className="gap-2"
          onClick={() => onAction('View candidate profile', interview)}
        >
          <User className="h-3.5 w-3.5" />
          View candidate profile
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          onClick={() => onAction('Add to my calendar', interview)}
        >
          <CalendarPlus className="h-3.5 w-3.5" />
          Add to my calendar
        </DropdownMenuItem>
        <DropdownMenuItem
          className="gap-2"
          onClick={() => onAction('Ask recruiter to reschedule', interview)}
        >
          <Clock className="h-3.5 w-3.5" />
          Ask recruiter to reschedule
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

function InterviewRow({ interview, onAction }) {
  const mode = interview.mode ? MODE_META[interview.mode] : null;
  const ModeIcon = mode?.icon;
  const rel = relativeLabel(interview.start);

  return (
    <div className="relative flex flex-col gap-3 rounded-xl border border-border bg-card p-4 pl-7 sm:flex-row sm:items-start sm:gap-5">
      <span
        className="absolute left-2.5 top-4 bottom-4 w-[3px] rounded-full bg-primary"
        aria-hidden="true"
      />
      <div className="flex shrink-0 flex-col gap-1 sm:w-[110px] sm:border-r sm:border-border sm:pr-4">
        <p
          className="text-[15px] font-bold text-foreground-strong"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {formatTime(interview.start)}
        </p>
        <p className="text-[12px] text-muted-foreground">
          {interview.durationMinutes} min
        </p>
        <p className="mt-1 flex items-center gap-1.5 text-[12px] text-foreground-muted">
          {ModeIcon && <ModeIcon className="h-3.5 w-3.5" />}
          {interview.modeLabel}
        </p>
        {rel && <p className="text-[11.5px] text-muted-foreground">{rel}</p>}
      </div>

      <div className="min-w-0 flex-1">
        <h3 className="text-[14.5px] font-semibold text-foreground-strong">
          {interview.candidateName}
        </h3>
        <p className="text-[12.5px] text-muted-foreground">
          {interview.role} · {interview.company}
        </p>
        <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-muted-foreground">
          <span className="text-foreground-muted">
            {interview.requirementTitle}
          </span>
          <span aria-hidden="true">·</span>
          <span className="font-mono">{interview.registrationId}</span>
        </p>
      </div>

      <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
        <div className="flex items-center gap-1.5">
          <Button
            variant="outline"
            size="headerSm"
            className="gap-1.5"
            onClick={() => onAction('Open brief', interview)}
          >
            <FileText className="h-3.5 w-3.5" />
            Open brief
          </Button>
          <RowMenu interview={interview} onAction={onAction} />
        </div>
      </div>
    </div>
  );
}

function InterviewGroupList({
  groups,
  onAction,
  emptyTitle,
  emptySubtitle,
  emptyAction,
  footerNote,
}) {
  if (groups.length === 0) {
    return (
      <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-[13.5px] text-foreground-strong">{emptyTitle}</p>
        {emptySubtitle && (
          <p className="mt-1 text-[12.5px] text-muted-foreground">
            {emptySubtitle}
          </p>
        )}
        {emptyAction}
      </div>
    );
  }
  return (
    <div className="space-y-6">
      {groups.map((group) => (
        <section key={group.key}>
          <div className="mb-2.5 flex items-baseline gap-2.5">
            <h2 className="text-[14px] font-semibold text-foreground-strong">
              {group.label}
            </h2>
            <span className="text-[12px] text-muted-foreground">
              {group.items.length}{' '}
              {group.items.length === 1 ? 'interview' : 'interviews'}
            </span>
          </div>
          <div className="flex flex-col gap-2.5">
            {group.items.map((iv) => (
              <InterviewRow key={iv.id} interview={iv} onAction={onAction} />
            ))}
          </div>
        </section>
      ))}
      {footerNote && (
        <p className="text-[12.5px] text-muted-foreground">{footerNote}</p>
      )}
    </div>
  );
}

function LoadingPlaceholder() {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center text-[13px] text-muted-foreground">
      Loading interviews…
    </div>
  );
}

function ErrorPlaceholder({ onRetry }) {
  return (
    <div className="rounded-xl border border-border bg-card p-8 text-center">
      <p className="text-[13.5px] text-foreground-strong">
        Couldn&apos;t load interviews.
      </p>
      <p className="mt-1 text-[12.5px] text-muted-foreground">
        Something went wrong talking to the server.
      </p>
      <Button variant="outline" size="sm" className="mt-4" onClick={onRetry}>
        Try again
      </Button>
    </div>
  );
}

// Collapses the loading/error/content three-way branch each tab needs
// without nesting ternaries.
function QuerySection({ query, children }) {
  if (query.isLoading) return <LoadingPlaceholder />;
  if (query.isError) return <ErrorPlaceholder onRetry={query.refetch} />;
  return children;
}

function InterviewsPagination({
  pagination,
  page,
  pageSize,
  onPageChange,
  onPageSizeChange,
}) {
  if (!pagination || pagination.total === 0) return null;
  return (
    <TablePagination
      className="mt-6"
      currentPage={page}
      totalPages={pagination.total_pages || 1}
      pageSize={pageSize}
      totalItems={pagination.total}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      itemLabel="interviews"
    />
  );
}

function UpcomingInterviews() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const { getUserInfo } = useAuth();
  const currentUserEmail = getUserInfo()?.email;

  const [activeTab, setActiveTab] = useState('upcoming');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState(undefined);

  // search/from_datetime/to_datetime are server-side filters now (the
  // listing endpoint's own route takes them directly) — debounced so typing
  // doesn't fire a request per keystroke, same pattern Opportunities.jsx
  // already uses for its own search box.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearchQuery(searchQuery), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);
  // Scorecard due / Completed only fetch once the interviewer actually opens
  // that tab — "upcoming" is the only one loaded on page mount — and stay
  // fetched afterwards so switching back doesn't refire the request.
  const [visitedTabs, setVisitedTabs] = useState({
    scorecard: false,
    completed: false,
  });

  // One shared "rows per page" across tabs, but each tab keeps its own
  // current page since they're independent server-paginated lists.
  const [pageSize, setPageSize] = useState(10);
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [scorecardPage, setScorecardPage] = useState(1);
  const [completedPage, setCompletedPage] = useState(1);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setVisitedTabs((prev) => (prev[tab] ? prev : { ...prev, [tab]: true }));
  };

  const handlePageSizeChange = (size) => {
    setPageSize(size);
    setUpcomingPage(1);
    setScorecardPage(1);
    setCompletedPage(1);
  };

  // Search/date-range are now server-side filters, so changing either has to
  // jump back to page 1 — otherwise a filter applied while sitting on, say,
  // page 3 could request a page past the end of the newly-filtered result
  // set. Keyed off the debounced search value so a mid-typing keystroke
  // doesn't reset the page before the request it belongs to even fires.
  useEffect(() => {
    setUpcomingPage(1);
  }, [debouncedSearchQuery, dateRange]);

  const fromDatetime = dateRange?.from
    ? startOfDay(dateRange.from).toISOString()
    : undefined;
  const toDatetime = dateRange?.from
    ? endOfDay(dateRange.to || dateRange.from).toISOString()
    : undefined;

  const upcomingQuery = useInterviewerInterviews('upcoming', {
    page: upcomingPage,
    pageSize,
    search: debouncedSearchQuery || undefined,
    fromDatetime,
    toDatetime,
  });
  const verdictPendingQuery = useInterviewerInterviews('verdict_pending', {
    page: scorecardPage,
    pageSize,
    enabled: visitedTabs.scorecard,
  });
  const completedQuery = useInterviewerInterviews('completed', {
    page: completedPage,
    pageSize,
    enabled: visitedTabs.completed,
  });

  const upcoming = useMemo(
    () =>
      (upcomingQuery.data?.interviews || []).map((r) =>
        mapInterview(r, currentUserEmail)
      ),
    [upcomingQuery.data, currentUserEmail]
  );
  const verdictPending = useMemo(
    () =>
      (verdictPendingQuery.data?.interviews || []).map((r) =>
        mapInterview(r, currentUserEmail)
      ),
    [verdictPendingQuery.data, currentUserEmail]
  );
  const completed = useMemo(
    () =>
      (completedQuery.data?.interviews || []).map((r) =>
        mapInterview(r, currentUserEmail)
      ),
    [completedQuery.data, currentUserEmail]
  );

  // "Next up" only means "the very next interview overall" on the
  // unfiltered first page — upcoming[0] on a later page or a filtered view
  // is just the first row of whatever's currently in view, not the actual
  // next one. search/dateRange are now server-side filters, so `upcoming`
  // itself is already the filtered list — this just decides whether it's
  // safe to call that first row "next up" at all.
  const nextUp =
    upcomingPage === 1 && !searchQuery && !dateRange?.from ? upcoming[0] : null;

  // Also shown in the "Next up" hero card above, but kept here too rather
  // than filtered out — per explicit instruction, the repeat doesn't matter.
  const groups = useMemo(() => buildGroups(upcoming), [upcoming]);
  const verdictPendingGroups = useMemo(
    () => buildGroups(verdictPending, true),
    [verdictPending]
  );
  const completedGroups = useMemo(
    () => buildGroups(completed, true),
    [completed]
  );

  const handleAction = (label, interview) => {
    // interview.id is the real interview_round_detail_id, the same id
    // InterviewBrief fetches its GET .../brief and .../questionnaire calls
    // with — so this lands on that exact interview's real brief.
    if (label === 'Open brief' && interview) {
      navigate(`/interviews/${interview.id}/brief`);
      return;
    }
    if (label === 'Join call' && interview?.joinLink) {
      window.open(interview.joinLink, '_blank', 'noopener,noreferrer');
      return;
    }
    toast({
      message: interview
        ? `${label} — ${interview.candidateName} (not wired up yet)`
        : `${label} (not wired up yet)`,
      variant: 'default',
    });
  };

  const getUpcomingEmptyTitle = () => {
    if (searchQuery) return `No upcoming interviews match "${searchQuery}".`;
    if (dateRange?.from)
      return 'No upcoming interviews in the selected date range.';
    return 'No upcoming interviews scheduled.';
  };

  const tabs = [
    { label: 'Upcoming', value: 'upcoming', statsLabel: 'upcoming' },
    { label: 'Scorecard due', value: 'scorecard', statsLabel: 'scorecard' },
    { label: 'Completed', value: 'completed', statsLabel: 'completed' },
  ];
  // The list endpoint returns a global `stats` object (upcoming /
  // verdict_pending / completed) alongside whichever single status was
  // actually requested — so it's present on upcomingQuery's response even
  // though the other two tabs' own queries are lazy (only enabled once
  // visited, per useInterviewerInterviews' own comment). Reading counts off
  // that instead of each tab's own pagination.total means the badges are
  // right from first load, not stuck at 0 until a tab has been opened once.
  const stats =
    upcomingQuery.data?.stats ||
    verdictPendingQuery.data?.stats ||
    completedQuery.data?.stats;
  const tabCounts = {
    upcoming:
      stats?.upcoming ??
      upcomingQuery.data?.pagination?.total ??
      upcoming.length,
    scorecard:
      stats?.verdict_pending ??
      verdictPendingQuery.data?.pagination?.total ??
      verdictPending.length,
    completed:
      stats?.completed ??
      completedQuery.data?.pagination?.total ??
      completed.length,
  };

  return (
    <div>
      <PageHeader title="Upcoming Interviews" />
      <PageContainer>
        <p className="text-[13.5px] text-muted-foreground max-w-[74ch]">
          Interviews you&apos;re on the panel for. Each one comes with a brief:
          the claims to verify and the gaps to clarify for that candidate.
        </p>
        {upcomingQuery.data && (
          <p className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-[12px] text-muted-foreground">
            <span>
              {upcomingQuery.data.pagination?.total ?? upcoming.length} upcoming
              interviews
            </span>
            <span aria-hidden="true">·</span>
            <span>Times in IST (UTC+05:30)</span>
          </p>
        )}

        {nextUp && (
          <div className="mt-6 rounded-xl border border-border bg-card p-5">
            <div className="flex items-center justify-between gap-3">
              <h2 className="text-[15px] font-semibold text-foreground-strong">
                Next up
              </h2>
              <span className="inline-flex items-center gap-1.5 rounded-md bg-amber-100 px-2.5 py-1 text-[11.5px] font-semibold text-amber-700">
                <Clock className="h-3.5 w-3.5" />
                {relativeLabel(nextUp.start)
                  ? `Starts ${relativeLabel(nextUp.start)} · ${dayGroupLabel(nextUp.start)}`
                  : dayGroupLabel(nextUp.start)}
              </span>
            </div>
            <div className="mt-4 flex flex-col gap-5 sm:flex-row">
              <div className="flex shrink-0 flex-col gap-1 sm:w-[172px] sm:border-r sm:border-border sm:pr-5">
                <p className="font-mono text-[22px] font-bold text-foreground-strong">
                  {formatTime(nextUp.start)}
                </p>
                <p className="text-[12.5px] text-muted-foreground">
                  {nextUp.durationMinutes} min
                </p>
                <p className="mt-1 flex items-center gap-1.5 text-[12.5px] text-foreground-muted">
                  {nextUp.mode &&
                    (() => {
                      const ModeIcon = MODE_META[nextUp.mode].icon;
                      return <ModeIcon className="h-3.5 w-3.5" />;
                    })()}
                  {nextUp.modeLabel}
                </p>
              </div>
              <div className="flex min-w-0 flex-1 gap-3.5">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-primary text-[13px] font-bold text-white">
                  {nextUp.candidateName
                    .split(' ')
                    .map((p) => p[0])
                    .slice(0, 2)
                    .join('')}
                </span>
                <div className="min-w-0">
                  <h3 className="text-[16px] font-semibold text-foreground-strong">
                    {nextUp.candidateName}
                  </h3>
                  <p className="text-[13px] text-muted-foreground">
                    {nextUp.role} · {nextUp.company}
                  </p>
                  <p className="mt-1 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[12px] text-muted-foreground">
                    <span>{nextUp.requirementTitle}</span>
                    <span aria-hidden="true">·</span>
                    <span className="font-mono">{nextUp.registrationId}</span>
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-4 flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-[12.5px] text-muted-foreground">
                {nextUp.panelLabel || 'Panel not confirmed yet'}
              </p>
              <div className="flex items-center gap-2">
                {nextUp.joinLink && (
                  <Button
                    variant="outline"
                    size="headerSm"
                    className="gap-1.5"
                    onClick={() => handleAction('Join call', nextUp)}
                  >
                    <Video className="h-3.5 w-3.5" />
                    Join call
                  </Button>
                )}
                <Button
                  size="headerSm"
                  className="gap-1.5"
                  onClick={() => handleAction('Open brief', nextUp)}
                >
                  <FileText className="h-3.5 w-3.5" />
                  Open brief
                </Button>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between">
          <FilterButton
            filters={tabs}
            defaultActiveFilter={activeTab}
            onFilterChange={handleTabChange}
            countStats={tabCounts}
          />
          {activeTab === 'upcoming' && (
            <div className="flex flex-col gap-2 sm:mb-2 sm:flex-row sm:items-center">
              <SearchInput
                value={searchQuery}
                onValueChange={setSearchQuery}
                placeholder="Search"
                className="w-full sm:w-62"
              />
              <DateRangePicker
                value={dateRange}
                onChange={setDateRange}
                className="w-full sm:w-72"
              />
            </div>
          )}
        </div>

        {activeTab === 'upcoming' && (
          <div className="mt-5">
            <QuerySection query={upcomingQuery}>
              <InterviewGroupList
                groups={groups}
                onAction={handleAction}
                emptyTitle={getUpcomingEmptyTitle()}
                emptySubtitle="Search by candidate name, company, requirement title or Reg ID."
                emptyAction={
                  (searchQuery || dateRange?.from) && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-4"
                      onClick={() => {
                        setSearchQuery('');
                        setDateRange(undefined);
                      }}
                    >
                      Clear filters
                    </Button>
                  )
                }
                footerNote={
                  groups.length > 0 &&
                  !searchQuery &&
                  !dateRange?.from &&
                  !upcomingQuery.data?.pagination?.has_next
                    ? "That's everything booked for now. New interviews show up here as soon as a recruiter schedules them."
                    : null
                }
              />
              <InterviewsPagination
                pagination={upcomingQuery.data?.pagination}
                page={upcomingPage}
                pageSize={pageSize}
                onPageChange={setUpcomingPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </QuerySection>
          </div>
        )}

        {activeTab === 'scorecard' && (
          <div className="mt-5">
            <QuerySection query={verdictPendingQuery}>
              <InterviewGroupList
                groups={verdictPendingGroups}
                onAction={handleAction}
                emptyTitle="No scorecards due."
                emptySubtitle="Interviews you've finished but haven't scored yet will show up here."
              />
              <InterviewsPagination
                pagination={verdictPendingQuery.data?.pagination}
                page={scorecardPage}
                pageSize={pageSize}
                onPageChange={setScorecardPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </QuerySection>
          </div>
        )}

        {activeTab === 'completed' && (
          <div className="mt-5">
            <QuerySection query={completedQuery}>
              <InterviewGroupList
                groups={completedGroups}
                onAction={handleAction}
                emptyTitle="No completed interviews yet."
              />
              <InterviewsPagination
                pagination={completedQuery.data?.pagination}
                page={completedPage}
                pageSize={pageSize}
                onPageChange={setCompletedPage}
                onPageSizeChange={handlePageSizeChange}
              />
            </QuerySection>
          </div>
        )}
      </PageContainer>
    </div>
  );
}

export default UpcomingInterviews;
